import { useState, FormEvent, useMemo } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { LayoutTypes } from "@enums/layout-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalSizeTypes } from "@enums/modal-size-types";
import { FormattedRpServiceConfigurationServiceOfferingAssertion } from "@idw-types/data/rp-service-configuration";
import { SelectOption } from "@idw-types/common";
import { sortStrings } from "@utils/string-util";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";
import { LabelValueContainer } from "../../common/label-value-container/label-value-container";

type AddRpServiceConfigurationServiceOfferingAssertionModalProps = {
    onModalClose: () => void;
    rpServiceConfigurationServiceOfferingAssertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>;
    addRpServiceConfigurationServiceOfferingAssertion: (rpServiceConfigurationServiceOfferingAssertion: FormattedRpServiceConfigurationServiceOfferingAssertion) => void;
};

type AddServiceOfferingAssertionLabelModalState = {
    rpServiceConfigurationServiceOfferingAssertion: FormattedRpServiceConfigurationServiceOfferingAssertion;
    selectedRpServiceConfigurationServiceOfferingAssertionOption: SelectOption;
};

export const AddRpServiceConfigurationServiceOfferingAssertionModal = (props: AddRpServiceConfigurationServiceOfferingAssertionModalProps) => {
    const { onModalClose, rpServiceConfigurationServiceOfferingAssertions, addRpServiceConfigurationServiceOfferingAssertion } = props;

    const [labelWidth] = useState(LabelWidthTypes.SMALL);
    const [state, setState] = useState<AddServiceOfferingAssertionLabelModalState>({
        rpServiceConfigurationServiceOfferingAssertion: null,
        selectedRpServiceConfigurationServiceOfferingAssertionOption: null,
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();


        addRpServiceConfigurationServiceOfferingAssertion({
            ...state.rpServiceConfigurationServiceOfferingAssertion,
            addedToService: true
        });
        onModalClose();
    };

    const handleServiceOfferingAssertionChange = (newValue: SelectOption) => {
        const rpServiceConfigurationServiceOfferingAssertion = rpServiceConfigurationServiceOfferingAssertions.find(assertion => assertion.apAssertionId === newValue.value);

        setState({
            rpServiceConfigurationServiceOfferingAssertion: rpServiceConfigurationServiceOfferingAssertion,
            selectedRpServiceConfigurationServiceOfferingAssertionOption: newValue,
        });
    };


    const assertionOptions = useMemo(() => {
        return rpServiceConfigurationServiceOfferingAssertions
            .map(assertion => ({ label: assertion.shortName, value: assertion.apAssertionId }))
            .sort((a, b) => sortStrings(a.label, b.label));
    }, [rpServiceConfigurationServiceOfferingAssertions]);

    return (
        <DefaultModal
            automationId="add-assertion-modal"
            ariaLabelledBy="add-assertion-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
            size={ModalSizeTypes.MEDIUM}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-assertion-title"} closeButton>
                    <Modal.Title>{resources.modals.addRpServiceConfigurationServiceOfferingAssertion.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SelectFormGroup
                        controlId={"assertion-select"}
                        automationId={"assertion-select"}
                        ariaLabel={resources.modals.addRpServiceConfigurationServiceOfferingAssertion.inputs.assertion.label}
                        label={resources.modals.addRpServiceConfigurationServiceOfferingAssertion.inputs.assertion.label}
                        disabled={false}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={true}
                        labelWidth={labelWidth}
                        onChange={handleServiceOfferingAssertionChange}
                        options={assertionOptions}
                        selectedOption={state.selectedRpServiceConfigurationServiceOfferingAssertionOption}
                    />
                    {state.selectedRpServiceConfigurationServiceOfferingAssertionOption && (
                        <LabelValueContainer
                            controlId={"assertion-label-value"}
                            label={resources.modals.addRpServiceConfigurationServiceOfferingAssertion.labels.description}
                            value={state.rpServiceConfigurationServiceOfferingAssertion.description}
                            labelWidth={labelWidth}
                            layout={LayoutTypes.ROW}
                            labelIsBold={false}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <Button
                        disabled={!state.selectedRpServiceConfigurationServiceOfferingAssertionOption}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                    >
                        {resources.modals.addRpServiceConfigurationServiceOfferingAssertion.buttons.addAssertion.label}
                    </Button>
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};